import { useState } from "react";
import { X, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";

const DeleteAccountModal = ({ onClose, userEmail }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailInput, setEmailInput] = useState("");

  const handleDelete = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.error("Authentication required. Please sign in again.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${BASE_URL}${API_ENDPOINTS.DELETE_MY_ACCOUNT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json().catch(() => null);
      const statusCode = result?.statusCode ?? response.status;

      if (!response.ok || statusCode >= 400) {
        throw new Error(result?.message || "Failed to delete account.");
      }

      toast.success("Your account has been deleted.");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("loginSuccess");
      setTimeout(() => {
        window.location.href = "/";
      }, 1000);
    } catch (error) {
      toast.error(error?.message || "Failed to delete account.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={!isSubmitting ? onClose : undefined}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="rounded-lg overflow-hidden shadow-2xl w-[80%] lg:w-[22%] bg-white">
          <div className="flex items-center justify-between p-6 text-black">
            <h2 className="text-lg lg:text-xl font-popinsSemiBold">Delete Account</h2>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="w-9 h-9 bg-white/20 hover:bg-white/30 rounded-full text-lg flex items-center justify-center transition-all hover:rotate-90 disabled:opacity-50"
            >
              <X className="w-6 h-6 text-foreground" />
            </button>
          </div>
          <div className="px-6 pb-6">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-7 h-7 text-red-500" />
              </div>
            </div>
            <p className="font-popinsRegular text-base text-center">
              Are you sure you want to delete your account?
            </p>
            <p className="font-popinsRegular text-sm text-center text-gray-500 mt-1">
              This action cannot be undone. You will lose access immediately.
            </p>
            <div className="mt-5">
              <p className="font-popinsRegular text-sm text-gray-600 mb-2">
                Type your email address to confirm:
              </p>
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder={userEmail}
                disabled={isSubmitting}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-popinsRegular focus:outline-none focus:border-red-400 disabled:opacity-50"
              />
            </div>
            <div className="flex justify-end gap-x-3 mt-6">
              <button
                type="button"
                className="relative overflow-hidden py-3 px-8 bg-[#F0F2F5] text-black font-popinsSemiBold text-sm rounded-md shadow-lg transform transition duration-300 ease-in-out hover:bg-[#F0F2F5]/90 hover:scale-105 active:scale-95 active:shadow-inner focus:outline-none disabled:opacity-50"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
                <span className="absolute inset-0 rounded-full bg-white opacity-0 active:opacity-10 transition-opacity duration-300"></span>
              </button>
              <button
                type="button"
                className="relative overflow-hidden py-3 px-8 bg-red-500 text-white font-popinsSemiBold text-sm rounded-md shadow-lg transform transition duration-300 ease-in-out hover:bg-red-600 hover:scale-105 active:scale-95 active:shadow-inner focus:outline-none disabled:opacity-50"
                onClick={handleDelete}
                disabled={isSubmitting || emailInput !== userEmail}
              >
                {isSubmitting ? "Deleting..." : "Delete Account"}
                <span className="absolute inset-0 rounded-full bg-white opacity-0 active:opacity-10 transition-opacity duration-300"></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeleteAccountModal;
